// EVERY RATE THAT MADE THE TOTAL, on the page itself.
//
// Owner, 2026-10-26: "why is the cost report doesn't show all unit costs used
// to create the total cost, it shouldn't only show the default rate, it should
// show all unique rates that have been used on all items, including specific
// overrides items".
//
// He had asked for this once already, and it was wired into the SENT EMAIL
// alone (utils/contractorReportSend "Rates used"). The page and the exported
// file still showed a total with no way to check it, which is what he is
// looking at. The server now returns `ratesUsed` on the report
// (utils/billableHours.ratesUsed - one rule, used by the email, both exports
// and this card), so nothing here sums anything of its own.
//
// A total alone cannot be checked by the person reading it. Two lines of the
// same contractor carry different rates for perfectly good reasons - an item
// rate, an activity rate, a project rate - and without this the reader is left
// to assume an error: "why is this priced at 85 instead of the default 55".
import React from "react";
import { Card, Table, Tag, Tooltip } from "antd";
import { RATE_SOURCE } from "./ContractorRows";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const hrs = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const T = { muted: "#667085", amber: "#b54708" };

// An override is a deliberate act someone took on a line; the recorded rate is
// what the ledger had. Marking which is which is the whole point of the card.
const IS_OVERRIDE = (s) => String(s || "").startsWith("override-");

export default function RatesUsed({ rates, estimated = false }) {
  const rows = Array.isArray(rates) ? rates : [];
  if (!rows.length) return null;

  const columns = [
    {
      title: "Rate",
      key: "rate",
      align: "right",
      width: 110,
      render: (_, g) => (g.rate == null
        ? <span style={{ color: T.amber }}>not set</span>
        : <b>{money(g.rate)}/h</b>),
    },
    {
      title: "Why this rate",
      key: "why",
      render: (_, g) => (
        <span>
          {RATE_SOURCE[g.source] || g.source}
          {IS_OVERRIDE(g.source) ? <Tag color="gold" style={{ marginLeft: 6 }}>override</Tag> : null}
          {g.replaces && g.replaces.length ? (
            <span style={{ color: T.muted }}>
              {" "}instead of {g.replaces.map((x) => `${money(x)}/h`).join(", ")}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      title: "Contractors",
      key: "who",
      render: (_, g) => {
        const who = g.contractors || [];
        if (!who.length) return <span style={{ color: T.muted }}>—</span>;
        if (who.length <= 2) return who.join(", ");
        return (
          <Tooltip title={who.join(", ")}>
            <span>{who.slice(0, 2).join(", ")} and {who.length - 2} more</span>
          </Tooltip>
        );
      },
    },
    { title: "Lines", dataIndex: "lines", key: "lines", align: "right", width: 80 },
    {
      title: estimated ? "Estimated hours" : "Hours",
      key: "hours",
      align: "right",
      width: 120,
      render: (_, g) => hrs(g.hours),
    },
    {
      title: "Cost",
      key: "cost",
      align: "right",
      width: 120,
      render: (_, g) => money(g.cost),
    },
  ];

  return (
    <Card
      size="small"
      style={{ marginBottom: 12 }}
      data-testid="rates-used"
      title={`Rates used (${rows.length})`}
      extra={(
        <span style={{ color: T.muted, fontSize: 12 }}>
          Every rate behind the total above, not just the default
        </span>
      )}
    >
      <Table
        size="small"
        rowKey={(g) => `${g.rate == null ? "none" : g.rate}|${g.source}`}
        dataSource={rows}
        columns={columns}
        pagination={false}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} align="right">
              <b>{rows.length} rate{rows.length === 1 ? "" : "s"}</b>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1}><b>TOTAL</b></Table.Summary.Cell>
            <Table.Summary.Cell index={2} />
            <Table.Summary.Cell index={3} align="right">
              <b>{rows.reduce((a, g) => a + (Number(g.lines) || 0), 0)}</b>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={4} align="right">
              <b>{hrs(rows.reduce((a, g) => a + (Number(g.hours) || 0), 0))}</b>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={5} align="right">
              <b>{money(rows.reduce((a, g) => a + (Number(g.cost) || 0), 0))}</b>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
      <div style={{ color: T.muted, fontSize: 12, marginTop: 6 }}>
        Each line&apos;s own rate and the reason for it are on the contractor&apos;s rows below, and in the exported file.
      </div>
    </Card>
  );
}
